import { useCallback, useState } from "react";
import { useParams } from "react-router";
import _ from "lodash";

import {
  ArrowDropDownIcon,
  ArrowRightIcon,
  DeleteOutlineOutlinedIcon,
  MoreVertIcon,
} from "icons";
import theme from "theme";
import { colors } from "theme/palette";
import { format, FORMATS, parseISO } from "utils/datetime";
import { handleInvalidRequest } from "utils/sdk";

import Button from "components/Button";
import ButtonDropdown from "components/ButtonDropdown";
import { MenuItem } from "components/Menu";
import Stack from "components/Stack";
import Text from "components/Text";
import toast from "components/Toast";
import { useConfirmationDialog } from "components/useConfirmationDialog";

import { TRANSACTION_EMPTY_VALUES } from "pages/Deal/FinancingEvents/constants";
import FinancingEventCollapse from "pages/Deal/FinancingEvents/FinancingEventCollapse";
import FinancingEventModifyDialogButton from "pages/Deal/FinancingEvents/FinancingEventModifyDialogButton";
import TransactionCreateDialogButton from "pages/Deal/FinancingEvents/TransactionModifyDialog/TransactionCreateDialogButton";

import {
  financingEventDelete,
  IFinancingEvent,
  useFinancingEvents,
} from "entities/Deal/sdk";

export interface IFinancingEventBox {
  event: IFinancingEvent;
}

const FinancingEventBox = ({ event }: IFinancingEventBox) => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const { show: showConfirmationDialog } = useConfirmationDialog();
  const params = useParams<{
    dealId: string;
  }>();

  const dealId = Number(params.dealId);

  const { mutate: mutateFinancingEvents } = useFinancingEvents({
    dealId,
  });

  const onFinancingEventDelete = useCallback(() => {
    showConfirmationDialog({
      onConfirm: () =>
        financingEventDelete({ dealId, financingEventId: event.id })
          .then(() => {
            toast.successMessage("Financing event successfully deleted");
            mutateFinancingEvents();
          })
          .catch(handleInvalidRequest),
      confirmButtonText: "Delete",
      message: "Are you sure you want to delete this event?",
    });
  }, [dealId, showConfirmationDialog, mutateFinancingEvents, event]);

  return (
    <>
      <Stack
        spacing={2}
        key={event.id}
        sx={{ backgroundColor: colors.blue20 }}
        borderRadius={2}
        flexDirection="row"
        padding={2}
      >
        <Stack gap={1} flexGrow={1}>
          <Stack flexDirection="row" gap={0.5}>
            <Stack onClick={() => setIsCollapsed((open) => !open)}>
              <Button
                sx={{
                  padding: theme.spacing(0.5, 0.75),
                  width: "auto",
                  justifyContent: "space-between",
                  whiteSpace: "nowrap",
                  minWidth: "max-content",
                  "& .MuiButton-endIcon": {
                    margin: 0,
                  },
                }}
                endIcon={
                  isCollapsed ? <ArrowDropDownIcon /> : <ArrowRightIcon />
                }
                color="secondary"
              />
            </Stack>
            <Text variant="h2">{event.name}</Text>
          </Stack>
          <Stack
            flexDirection="row"
            gap={1}
            paddingLeft={4}
            alignItems="center"
          >
            <Text
              sx={{ backgroundColor: colors.white }}
              border={1}
              borderColor={colors.blue60}
              padding={theme.spacing(0.2, 1)}
              variant="text2"
              fontWeight={600}
            >
              {event.event_type}
            </Text>
            {!_.isNil(event.closing_date) && (
              <Text variant="text2" color={colors.gray80}>
                Closing date:{" "}
                {format(parseISO(event.closing_date), FORMATS.FRONTEND.DATE)}
              </Text>
            )}
            <Text
              borderRadius={4}
              sx={{ backgroundColor: colors.white }}
              padding={theme.spacing(0.2, 1)}
              border={1}
              borderColor={colors.blue60}
              variant="text2"
              fontWeight={600}
            >
              {event.transaction_count} transaction
              {event.transaction_count === 1 ? "" : "s"}
            </Text>
          </Stack>
        </Stack>
        <Stack flexDirection="row" gap={2}>
          <ButtonDropdown
            icon={<MoreVertIcon />}
            label=""
            variant="text"
            color="secondary"
            width="auto"
            keepMenuMounted={true}
            sx={{
              padding: theme.spacing(0.5, 0.75),
              "& .MuiButton-endIcon": {
                margin: 0,
              },
            }}
          >
            <FinancingEventModifyDialogButton
              financingEventId={event.id}
              initialValues={{
                name: event.name,
                event_type: event.event_type,
                closing_date: event.closing_date,
              }}
            />
            <MenuItem onClick={onFinancingEventDelete}>
              <Stack gap={1} flexDirection="row" alignItems="center">
                <DeleteOutlineOutlinedIcon />
                <Text variant="text2">Delete event</Text>
              </Stack>
            </MenuItem>
          </ButtonDropdown>
          <TransactionCreateDialogButton
            initialValues={TRANSACTION_EMPTY_VALUES}
            financingEventId={event.id}
          />
        </Stack>
      </Stack>
      <FinancingEventCollapse financingEventId={event.id} open={isCollapsed} />
    </>
  );
};

export default FinancingEventBox;
